import type { Todo } from "@/types/todo";
import { type AgeSortType, parseTaskSortType, sortTasksByAge } from "@/utils/taskUrgency";

/**
 * Shared todo list filter criteria used by the service layer and Electric
 * client-side filtering so Project / Client / Category aggregators actually apply.
 */
export interface TodoFilterCriteria {
  searchTerm?: string;
  status?: string;
  priority?: string;
  includeCompleted?: boolean;
  parentOnly?: boolean;
  assignmentFilter?: "all" | "created_by_me" | "assigned_to_me";
  createdByUserId?: string;
  assignedToUserId?: string;
  categoryId?: string;
  clientId?: string;
  projectId?: string;
  currentUserId?: string;
  dateRange?: {
    start: string;
    end: string;
  };
}

export const TASK_GROUPING_SORTS = [
  "status",
  "priority",
  "project",
  "client",
  "category",
] as const;

export type TaskGroupingSort = (typeof TASK_GROUPING_SORTS)[number];

export interface TaskGroupLabels {
  projects?: Record<string, string>;
  clients?: Record<string, string>;
  categories?: Record<string, string>;
}

export interface TaskGroup {
  key: string;
  label: string;
  tasks: Todo[];
}

const PRIORITY_GROUP_ORDER = ["urgent", "high", "medium", "low"] as const;
const STATUS_GROUP_ORDER = ["todo", "in_progress", "completed"] as const;

export function normalizeTodoStatus(status?: string): string | undefined {
  if (!status || status === "all") {
    return undefined;
  }
  if (status === "in-progress") {
    return "in_progress";
  }
  return status;
}

export function isTaskGroupingSort(
  value: string | undefined | null
): value is TaskGroupingSort {
  return TASK_GROUPING_SORTS.includes(value as TaskGroupingSort);
}

/**
 * Apply list filters in memory. Used as the canonical filter engine for
 * `getFilteredTodos` and Electric-backed hooks.
 */
export function applyTodoFilters(
  todos: Todo[],
  filters: TodoFilterCriteria = {}
): Todo[] {
  let filtered = todos;
  const status = normalizeTodoStatus(filters.status);
  const search = filters.searchTerm?.trim().toLowerCase();

  if (filters.parentOnly) {
    filtered = filtered.filter((todo) => !todo.parent_id);
  }

  if (search) {
    filtered = filtered.filter(
      (todo) =>
        todo.title.toLowerCase().includes(search) ||
        (todo.description?.toLowerCase().includes(search) ?? false)
    );
  }

  if (status) {
    filtered = filtered.filter((todo) => todo.status === status);
  }

  if (filters.priority && filters.priority !== "all") {
    filtered = filtered.filter((todo) => todo.priority === filters.priority);
  }

  if (filters.createdByUserId) {
    filtered = filtered.filter((todo) => todo.created_by === filters.createdByUserId);
  }

  if (filters.assignedToUserId) {
    filtered = filtered.filter((todo) => todo.assigned_to === filters.assignedToUserId);
  }

  if (filters.categoryId) {
    filtered = filtered.filter((todo) => todo.category_id === filters.categoryId);
  }

  if (filters.clientId) {
    filtered = filtered.filter((todo) => todo.client_id === filters.clientId);
  }

  if (filters.projectId) {
    filtered = filtered.filter((todo) => todo.project_id === filters.projectId);
  }

  if (
    !filters.createdByUserId &&
    !filters.assignedToUserId &&
    filters.assignmentFilter &&
    filters.assignmentFilter !== "all" &&
    filters.currentUserId
  ) {
    if (filters.assignmentFilter === "created_by_me") {
      filtered = filtered.filter((todo) => todo.created_by === filters.currentUserId);
    } else if (filters.assignmentFilter === "assigned_to_me") {
      filtered = filtered.filter((todo) => todo.assigned_to === filters.currentUserId);
    }
  }

  // Explicit "completed" status should still show completed tasks.
  if (filters.includeCompleted !== true && status !== "completed") {
    filtered = filtered.filter((todo) => todo.status !== "completed");
  }

  if (filters.dateRange) {
    const start = new Date(filters.dateRange.start).getTime();
    const end = new Date(filters.dateRange.end).getTime();
    filtered = filtered.filter((todo) => {
      if (!todo.due_date) {
        return false;
      }
      const due = new Date(todo.due_date).getTime();
      return due >= start && due <= end;
    });
  }

  return filtered;
}

function unlabeledGroup(kind: TaskGroupingSort): { key: string; label: string } {
  switch (kind) {
    case "project":
      return { key: "none", label: "No project" };
    case "client":
      return { key: "none", label: "No client" };
    case "category":
      return { key: "none", label: "Uncategorized" };
    case "status":
      return { key: "none", label: "Unknown status" };
    case "priority":
      return { key: "none", label: "No priority" };
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

function groupKeyAndLabel(
  todo: Todo,
  groupBy: TaskGroupingSort,
  labels: TaskGroupLabels
): { key: string; label: string } {
  switch (groupBy) {
    case "project": {
      const id = todo.project_id;
      if (!id) {
        return unlabeledGroup(groupBy);
      }
      return { key: id, label: labels.projects?.[id] ?? "Project" };
    }
    case "client": {
      const id = todo.client_id;
      if (!id) {
        return unlabeledGroup(groupBy);
      }
      return { key: id, label: labels.clients?.[id] ?? "Client" };
    }
    case "category": {
      const id = todo.category_id;
      if (!id) {
        return unlabeledGroup(groupBy);
      }
      return {
        key: id,
        label: todo.category?.name ?? labels.categories?.[id] ?? "Category",
      };
    }
    case "status": {
      const status = todo.status || "todo";
      const statusLabels: Record<string, string> = {
        todo: "To Do",
        in_progress: "In Progress",
        completed: "Completed",
      };
      return { key: status, label: statusLabels[status] ?? status };
    }
    case "priority": {
      const priority = todo.priority || "medium";
      const priorityLabels: Record<string, string> = {
        urgent: "Urgent",
        high: "High",
        medium: "Medium",
        low: "Low",
      };
      return {
        key: priority,
        label: priorityLabels[priority] ?? priority,
      };
    }
    default: {
      const _exhaustive: never = groupBy;
      return _exhaustive;
    }
  }
}

function compareGroupKeys(
  a: TaskGroup,
  b: TaskGroup,
  groupBy: TaskGroupingSort
): number {
  if (a.key === "none" && b.key !== "none") {
    return 1;
  }
  if (b.key === "none" && a.key !== "none") {
    return -1;
  }

  if (groupBy === "status") {
    return (
      STATUS_GROUP_ORDER.indexOf(a.key as (typeof STATUS_GROUP_ORDER)[number]) -
      STATUS_GROUP_ORDER.indexOf(b.key as (typeof STATUS_GROUP_ORDER)[number])
    );
  }

  if (groupBy === "priority") {
    return (
      PRIORITY_GROUP_ORDER.indexOf(a.key as (typeof PRIORITY_GROUP_ORDER)[number]) -
      PRIORITY_GROUP_ORDER.indexOf(b.key as (typeof PRIORITY_GROUP_ORDER)[number])
    );
  }

  return a.label.localeCompare(b.label);
}

/**
 * Bucket tasks for aggregator sorts (project, client, category, status, priority).
 * Non-grouping sorts return a single unlabeled group.
 */
export function groupTasks(
  todos: Todo[],
  sortType: AgeSortType | "none" | string,
  labels: TaskGroupLabels = {},
  ascending = false
): TaskGroup[] {
  const normalized = parseTaskSortType(sortType);

  if (!isTaskGroupingSort(normalized)) {
    const sorted =
      normalized === "none"
        ? sortTasksByAge(todos, "created", ascending)
        : sortTasksByAge(todos, normalized, ascending);
    return [{ key: "all", label: "", tasks: sorted }];
  }

  const buckets = new Map<string, TaskGroup>();

  for (const todo of todos) {
    const { key, label } = groupKeyAndLabel(todo, normalized, labels);
    const existing = buckets.get(key);
    if (existing) {
      existing.tasks.push(todo);
    } else {
      buckets.set(key, { key, label, tasks: [todo] });
    }
  }

  const groups = Array.from(buckets.values()).map((group) => ({
    ...group,
    tasks: sortTasksByAge(group.tasks, "created", ascending),
  }));

  groups.sort((a, b) => compareGroupKeys(a, b, normalized));

  return groups;
}
